import { LoadingManager } from 'three';
import { setCounts, markFinished } from './loadProgress';

// How long the overlay waits with nothing at all happening before giving up
// and showing the scene anyway. Measured between items, not from page load.
export const LOAD_TIMEOUT_MS = 45000;

/**
 * A LoadingManager wired to the load-progress store.
 *
 * Pass this to every loader that loads part of the opening scene so the
 * overlay reports one honest percentage for the whole thing.
 */
export function createLoadingManager() {
  const manager = new LoadingManager();
  const failed = new Set();

  let total = 0;
  let loaded = 0;
  let timer = null;

  function publish() {
    setCounts({ loaded, failed: failed.size, total });
  }

  function isComplete() {
    return total > 0 && loaded + failed.size >= total;
  }

  function stopTimer() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  // The deadline measures silence, not total duration: the opening scene pulls
  // tens of MB, so a slow connection can legitimately outlast LOAD_TIMEOUT_MS
  // overall. Re-arming on every reported item means only a genuine stall — no
  // asset completing or failing for that long — ends the wait early.
  function armTimer() {
    stopTimer();
    timer = setTimeout(() => {
      timer = null;
      markFinished();
    }, LOAD_TIMEOUT_MS);
  }

  function settle(completed) {
    if (completed) {
      stopTimer();
    } else {
      armTimer();
    }
  }

  // onStart fires only for the first item, and is the only early signal of how
  // many items there will be — without it a scene whose very first asset fails
  // would never reach a total and would wait for the timeout.
  manager.onStart = (url, itemsLoaded, itemsTotal) => {
    total = itemsTotal;
    armTimer();
    publish();
  };

  manager.onProgress = (url, itemsLoaded, itemsTotal) => {
    loaded = itemsLoaded;
    total = itemsTotal;

    settle(isComplete());
    publish();
  };

  // LoadingManager.itemError does not call itemEnd, so a failed asset would
  // otherwise leave itemsLoaded short of itemsTotal and onLoad would never
  // fire. Counting it as completed is what keeps the overlay from hanging.
  manager.onError = (url) => {
    failed.add(url);

    settle(isComplete());
    publish();
  };

  manager.onLoad = () => {
    stopTimer();
    publish();
  };

  armTimer();

  return manager;
}

let shared = null;

/**
 * The one manager shared by every loader in the opening scene.
 */
export function getLoadingManager() {
  if (shared === null) {
    shared = createLoadingManager();
  }

  return shared;
}
